import { Trans, useLingui } from "@lingui/react/macro";

import { share } from "../../model/change";
import type { DashboardInviter, DashboardSource } from "../../model/TeamDashboard";
import { DashboardCard } from "./DashboardCard";

// Справочник источников в Django admin: новый канал, блогер или кампания — новая строка.
const SOURCES_ADMIN = "/admin/dashboard/acquisitionsource/";

type SourcesProps = {
  sources: DashboardSource[];
  topInviter: DashboardInviter | null | undefined;
  // Новые игроки, пришедшие во встроенном браузере приложения (TikTok, Instagram…).
  inAppPlayers: number;
};

// «Откуда приходят»: источники привлечения из справочника, приглашения друзей, прямые заходы,
// другие сайты и игроки без данных. Посетители — Umami, новые игроки — откуда пришли перед
// первым входом, «Вошли» — доля посетителей, которые вошли через Steam. Пришедшие во
// встроенном браузере приложения посчитаны в его источнике (бэкенд), внизу — сколько их.
export const Sources = ({ sources, topInviter, inAppPlayers }: SourcesProps) => {
  const { i18n, t } = useLingui();
  const number = new Intl.NumberFormat(i18n.locale);
  const percent = new Intl.NumberFormat(i18n.locale, {
    style: "percent",
    maximumFractionDigits: 1,
  });
  const most = Math.max(1, ...sources.map((row) => row.new_players));
  const inApp = number.format(inAppPlayers);
  const inviter = topInviter?.display_name;
  const invited = topInviter?.invited;
  const label = (row: DashboardSource): string => {
    switch (row.origin) {
      case "source":
        return row.name ?? row.code ?? "—";
      case "invite":
        return t`Friend invites`;
      case "direct":
        return t`Direct visits`;
      case "other":
        return t`Other sites`;
      case "unknown":
        return t`Unknown (before tracking)`;
    }
  };

  return (
    <DashboardCard
      title={<Trans>Where they come from</Trans>}
      description={
        <Trans>Visitors — Umami; new players — where they came from before the first sign-in</Trans>
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[360px] border-collapse text-[13px]">
          <thead>
            <tr className="text-left text-xs text-subtle">
              <th scope="col" className="pt-1 pr-2 pb-2 font-semibold">
                <Trans>Source</Trans>
              </th>
              <th scope="col" className="px-2 pt-1 pb-2 text-right font-semibold">
                <Trans>Visitors</Trans>
              </th>
              <th scope="col" className="px-2 pt-1 pb-2 text-right font-semibold">
                <Trans>New players</Trans>
              </th>
              <th scope="col" className="pt-1 pb-2 pl-2 text-right font-semibold">
                <Trans>Signed in</Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {sources.map((row) => {
              const conversion = share(row.new_players, row.visitors);
              return (
                <tr
                  key={`${row.origin}:${row.code ?? ""}`}
                  data-origin={row.origin}
                  data-source={row.code ?? undefined}
                  className="border-t border-border/60"
                >
                  <th
                    scope="row"
                    className="py-1.5 pr-2 text-left font-medium text-foreground-soft"
                  >
                    <span className="flex flex-col gap-1">
                      <span>{label(row)}</span>
                      <span aria-hidden="true" className="flex h-1">
                        {row.new_players > 0 && (
                          <span
                            className="h-1 rounded-r-sm bg-chart-amber"
                            style={{ width: `${String((row.new_players / most) * 100)}%` }}
                          />
                        )}
                      </span>
                    </span>
                  </th>
                  <td className="px-2 py-1.5 text-right font-mono tabular-nums">
                    {row.visitors === null ? "—" : number.format(row.visitors)}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono font-semibold tabular-nums">
                    {number.format(row.new_players)}
                  </td>
                  <td className="py-1.5 pl-2 text-right font-mono text-muted tabular-nums">
                    {conversion === null ? "—" : percent.format(conversion)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex flex-col gap-1 border-t border-border/60 pt-2.5 text-[13px] text-muted">
        <p data-value="in-app">
          <Trans>
            New players who came through an app’s built-in browser (TikTok, Instagram…):{" "}
            <strong className="text-foreground">{inApp}</strong> — they are counted under their
            source, not as direct visits. Umami can’t tell such visitors apart.
          </Trans>
        </p>
        {topInviter != null && (
          <p>
            <Trans>
              Invited the most: <strong className="text-foreground">{inviter}</strong> — {invited}.
            </Trans>
          </p>
        )}
        <p>
          <Trans>
            A new source is added in the{" "}
            <a href={SOURCES_ADMIN} target="_blank" rel="noopener" className="text-accent">
              admin, “Acquisition sources”
            </a>
            : its domains and a tag; the link looks like relock.gg/?utm_source=tag.
          </Trans>
        </p>
      </div>
    </DashboardCard>
  );
};
